let ctx;
export const isMuted = () => localStorage.getItem("jp_mute") === "1";
export const setMuted = (v) => localStorage.setItem("jp_mute", v ? "1" : "0");

const ac = () => {
  if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
  if (ctx.state === "suspended") ctx.resume();
  return ctx;
};

function tone(freq, dur, { type = "sine", vol = 0.2, when = 0, slide } = {}) {
  if (isMuted()) return;
  const c = ac();
  const t = c.currentTime + when;
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = type;
  o.frequency.setValueAtTime(freq, t);
  if (slide) o.frequency.exponentialRampToValueAtTime(slide, t + dur);
  g.gain.setValueAtTime(vol, t);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  o.connect(g).connect(c.destination);
  o.start(t);
  o.stop(t + dur + 0.05);
}

function noise(dur, { vol = 0.3, when = 0, freq = 1000, type = "lowpass", attack = 0.005 } = {}) {
  if (isMuted()) return;
  const c = ac();
  const t = c.currentTime + when;
  const buf = c.createBuffer(1, Math.floor(c.sampleRate * dur), c.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  const src = c.createBufferSource();
  src.buffer = buf;
  const f = c.createBiquadFilter();
  f.type = type;
  f.frequency.value = freq;
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.linearRampToValueAtTime(vol, t + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  src.connect(f).connect(g).connect(c.destination);
  src.start(t);
}

const arp = (notes, step, opts) => notes.forEach((f, i) => tone(f, step * 2, { ...opts, when: i * step }));

export const sfx = {
  click: () => tone(900, 0.05, { type: "square", vol: 0.04 }),
  reelTick: () => tone(1100 + Math.random() * 400, 0.025, { type: "square", vol: 0.02 }),
  reelStop: () => { tone(160, 0.14, { type: "triangle", vol: 0.3, slide: 60 }); noise(0.05, { vol: 0.08, freq: 2500 }); },
  win: () => arp([523, 659, 784, 1047], 0.08, { type: "triangle", vol: 0.14 }),
  bigWin: () => {
    arp([523, 659, 784, 1047, 784, 1047, 1319, 1568], 0.1, { type: "triangle", vol: 0.15 });
    for (let i = 0; i < 10; i++) tone(1800 + Math.random() * 1200, 0.08, { type: "square", vol: 0.03, when: 0.2 + i * 0.12 });
  },
  bonus: () => {
    arp([392, 523, 659, 784, 1047, 1319], 0.12, { type: "sawtooth", vol: 0.08 });
    arp([196, 262, 330, 392], 0.24, { type: "triangle", vol: 0.12 });
    noise(1.5, { vol: 0.12, freq: 6000, type: "highpass", attack: 0.4 });
  },
  gem: () => { tone(1320, 0.15, { vol: 0.12 }); tone(1760, 0.3, { vol: 0.1, when: 0.06 }); },
  explode: () => { noise(1.0, { vol: 0.7, freq: 500 }); tone(110, 0.8, { type: "sawtooth", vol: 0.25, slide: 30 }); },
  cashout: () => { arp([784, 1047, 1568], 0.07, { type: "square", vol: 0.06 }); tone(2093, 0.4, { vol: 0.08, when: 0.2 }); },
  kick: () => { tone(160, 0.14, { vol: 0.6, slide: 45 }); noise(0.06, { vol: 0.25, freq: 3000 }); },
  whistle: () => { tone(2900, 0.18, { vol: 0.1 }); tone(2950, 0.5, { vol: 0.1, when: 0.22 }); },
  cheer: () => { noise(2.6, { vol: 0.4, freq: 1200, type: "bandpass", attack: 0.25 }); noise(2.2, { vol: 0.15, freq: 4000, type: "highpass", attack: 0.3, when: 0.1 }); },
  lose: () => arp([392, 330, 262], 0.12, { type: "triangle", vol: 0.1 }),
  peg: () => tone(1500 + Math.random() * 900, 0.04, { type: "sine", vol: 0.06 }),
  flip: () => noise(0.12, { vol: 0.15, freq: 3000, type: "bandpass" }),
  groan: () => { noise(1.4, { vol: 0.25, freq: 350, attack: 0.25 }); tone(200, 1.0, { type: "sawtooth", vol: 0.04, slide: 120 }); },
};

export function engine() {
  if (isMuted()) return { set() {}, stop() {} };
  const c = ac();
  const g = c.createGain();
  g.gain.value = 0.0001;
  g.gain.linearRampToValueAtTime(0.05, c.currentTime + 0.4);
  const f = c.createBiquadFilter();
  f.type = "lowpass";
  f.frequency.value = 700;
  const o1 = c.createOscillator();
  const o2 = c.createOscillator();
  o1.type = "sawtooth";
  o2.type = "square";
  o1.frequency.value = 70;
  o2.frequency.value = 71.5;
  o1.connect(f); o2.connect(f); f.connect(g).connect(c.destination);
  o1.start(); o2.start();
  return {
    set(m) {
      const fr = Math.min(70 + Math.log(m) * 60, 320);
      o1.frequency.setTargetAtTime(fr, c.currentTime, 0.1);
      o2.frequency.setTargetAtTime(fr * 1.02, c.currentTime, 0.1);
    },
    stop() {
      g.gain.cancelScheduledValues(c.currentTime);
      g.gain.setTargetAtTime(0.0001, c.currentTime, 0.08);
      o1.stop(c.currentTime + 0.5); o2.stop(c.currentTime + 0.5);
    },
  };
}
